import * as React from "react";
import { format, parse, isValid } from "date-fns";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";

const VALUE_FORMAT = "yyyy-MM-dd";
const DISPLAY_FORMAT = "MM/dd/yyyy";
const CURRENT_YEAR = new Date().getFullYear();
const FROM_YEAR = CURRENT_YEAR - 10;
const TO_YEAR = CURRENT_YEAR + 30;

export interface DatePickerProps {
  value: string | null;
  onChange: (value: string | null) => void;
  /** Fires whenever the typed text has an unresolved format error (not yet committed to onChange) */
  onValidityChange?: (hasError: boolean) => void;
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  className?: string;
}

function parseValue(value: string | null): Date | undefined {
  if (!value) return undefined;
  const parsed = parse(value, VALUE_FORMAT, new Date());
  return isValid(parsed) ? parsed : undefined;
}

function formatDisplay(value: string | null): string {
  const parsed = parseValue(value);
  return parsed ? format(parsed, DISPLAY_FORMAT) : "";
}

export const DatePicker = React.forwardRef<HTMLDivElement, DatePickerProps>(function DatePicker(
  { value, onChange, onValidityChange, placeholder = "MM/DD/YYYY", id, disabled, className },
  ref,
) {
  const [open, setOpen] = React.useState(false);
  const [text, setText] = React.useState(() => formatDisplay(value));
  const [error, setError] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const selected = parseValue(value);

  React.useEffect(() => {
    onValidityChange?.(!!error);
  }, [error, onValidityChange]);

  // Resync the typed text whenever the committed value changes from outside
  // (initial load, or a day picked in the calendar) — but not on every
  // keystroke, since we haven't called onChange yet at that point.
  React.useEffect(() => {
    setText(formatDisplay(value));
    setError(null);
  }, [value]);

  const commitText = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) {
      setText("");
      setError(null);
      onChange(null);
      return;
    }
    if (!/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed)) {
      // Ambiguous/incomplete year (e.g. "7/4/26") — leave the text as typed
      // so the user can fix it, and tell them what's expected
      setError("Use MM/DD/YYYY with a 4-digit year, e.g. 07/04/2026.");
      return;
    }
    const parsed = parse(trimmed, DISPLAY_FORMAT, new Date());
    if (isValid(parsed)) {
      onChange(format(parsed, VALUE_FORMAT));
      setText(format(parsed, DISPLAY_FORMAT));
      setError(null);
    } else {
      // Valid shape but not a real date (e.g. 13/45/2026) — same treatment
      setError("That date doesn't exist — check the day and month.");
    }
  };

  return (
    <div ref={ref} className={cn("flex flex-col gap-1", className)}>
      <div className="flex gap-1.5">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverAnchor asChild>
            <Input
              ref={inputRef}
              id={id}
              type="text"
              placeholder={placeholder}
              value={text}
              disabled={disabled}
              aria-invalid={!!error}
              onFocus={() => setOpen(true)}
              onChange={(e) => {
                setText(e.target.value);
                if (error) setError(null);
              }}
              onBlur={(e) => commitText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitText(text);
                }
              }}
              className={cn("flex-1", error && "border-destructive focus-visible:ring-destructive")}
            />
          </PopoverAnchor>
          <PopoverContent
            className="w-auto p-0"
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
            onInteractOutside={(e) => {
              // The input is an Anchor, not a Trigger, so Radix doesn't know
              // it's part of this popover's own UI — without this, the same
              // click that focuses the input and opens the popover gets
              // treated as an outside interaction and closes it immediately.
              if (inputRef.current && e.target instanceof Node && inputRef.current.contains(e.target)) {
                e.preventDefault();
              }
            }}
          >
            <Calendar
              mode="single"
              selected={selected}
              defaultMonth={selected}
              captionLayout="dropdown-buttons"
              fromYear={FROM_YEAR}
              toYear={TO_YEAR}
              formatters={{ formatMonthCaption: (month) => format(month, "MM") }}
              onSelect={(date) => {
                if (date) {
                  onChange(format(date, VALUE_FORMAT));
                  setText(format(date, DISPLAY_FORMAT));
                } else {
                  onChange(null);
                  setText("");
                }
                setError(null);
                setOpen(false);
              }}
            />
          </PopoverContent>
        </Popover>
        {selected && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Clear date"
            disabled={disabled}
            onClick={() => {
              setText("");
              setError(null);
              onChange(null);
            }}
            className="shrink-0"
          >
            <X className="h-4 w-4 opacity-50" />
          </Button>
        )}
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
});
DatePicker.displayName = "DatePicker";
